/**
 * F14.1 — REGISTRO COMÚN DE OPERACIONES OFFLINE
 *
 * Una sola cola (`db.ts::mutaciones`), un solo motor (`sync.ts`) y, aquí, la
 * DECLARACIÓN de cada tipo de operación que puede viajar por ella. Antes de
 * F14.1 el motor conocía los tipos de Limpieza a mano (`tipo !==
 * 'EVIDENCIA_SUBIR'`, `'evento' in r`…); ahora pregunta al registro:
 *
 *   · `transporte: 'LOTE'`   viaja en `POST /api/sync` (JSON, por lotes, con
 *                            `revisionBase` y `dependeDe`); el registro dice
 *                            cómo reconciliar un APLICADA/YA_APLICADA.
 *   · `transporte: 'PROPIO'` tiene su propio envío (p. ej. una evidencia es
 *                            multipart); el registro aporta `sincronizar`.
 *
 * Añadir un dominio (Mantenimiento en F14.3, Inventario móvil en F14.4) es
 * registrar sus tipos en un módulo propio (`operaciones-<dominio>.ts`), no
 * tocar el motor. Este módulo NO importa ningún dominio ni `db.ts`: sólo
 * tipos, para que cualquiera pueda importarlo sin ciclos.
 */
import type { Mutacion, TipoMutacion } from './db.ts';

export type TransporteOperacion = 'LOTE' | 'PROPIO';

/** Lo que el servidor devuelve para una operación aplicada (o ya aplicada) del lote. Cada dominio lee su forma. */
export type ResultadoAplicadoLote = { readonly idOperacionCliente: string; readonly revisionServidorActual: string } & Readonly<Record<string, unknown>>;

/** Contadores de una ronda; una operación de transporte PROPIO suma el suyo. */
export interface ResumenParcial {
  aplicadas: number;
  yaAplicadas: number;
  rechazadas: number;
  errorTemporal: number;
  omitidas: number;
}

/** Datos que se guardan en la operación en la misma escritura que la marca SINCRONIZADA. */
export interface DatosAplicada {
  readonly resultadoNc?: Mutacion['resultadoNc'];
  /** F14.3 — ids reales de una operación de mantenimiento (intervención, nota, consumo). */
  readonly resultadoMnt?: Mutacion['resultadoMnt'];
}

export interface DefinicionOperacion {
  readonly tipo: TipoMutacion;
  /** Para el usuario: «Confirmación», «Evidencia»… — nunca el código. */
  readonly etiqueta: string;
  readonly dominio: 'LIMPIEZA' | 'CALIDAD' | 'MANTENIMIENTO' | 'INVENTARIO' | 'COMUN';
  /**
   * Versión del contrato del payload. Un cambio incompatible sube la versión y el registro decide qué hacer con
   * las operaciones antiguas aún en cola (nunca se descartan en silencio).
   */
  readonly versionPayload: number;
  readonly transporte: TransporteOperacion;
  /** Comprobación local ANTES de encolar (forma del payload). `null` = válido; un texto = por qué no. El servidor revalida siempre. */
  readonly validarLocal?: (payload: Mutacion['payload']) => string | null;
  /**
   * LOTE: lo que tiene que quedar guardado en la PROPIA operación junto con SINCRONIZADA (p. ej. el `idNc` real de
   * una NC creada offline). El motor lo escribe en la MISMA escritura que el estado: nunca puede quedar una
   * operación SINCRONIZADA sin esos datos aunque la página se cierre a mitad de la ronda (de ellos dependen las
   * operaciones posteriores, y una SINCRONIZADA no se reenvía nunca).
   */
  readonly datosAplicada?: (m: Mutacion, r: ResultadoAplicadoLote) => DatosAplicada;
  /**
   * LOTE: efectos sobre OTROS almacenes locales tras APLICADA/YA_APLICADA (p. ej. el estado de la línea en el
   * paquete), una vez la operación ya está SINCRONIZADA. Son reconstruibles (el siguiente paquete los repone).
   */
  readonly reconciliarAplicada?: (idPerfil: string, m: Mutacion, r: ResultadoAplicadoLote) => Promise<void>;
  /** PROPIO: envía UNA operación y deja su estado resuelto (nunca lanza: aísla sus fallos). */
  readonly sincronizar?: (idPerfil: string, m: Mutacion, resumen: ResumenParcial) => Promise<void>;
}

const registro = new Map<TipoMutacion, DefinicionOperacion>();

export function registrarOperacion(def: DefinicionOperacion): void {
  if (def.transporte === 'LOTE' && def.reconciliarAplicada === undefined && def.datosAplicada === undefined) {
    throw new Error(`Operación ${def.tipo}: el transporte LOTE necesita datosAplicada o reconciliarAplicada.`);
  }
  if (def.transporte === 'PROPIO' && def.sincronizar === undefined) {
    throw new Error(`Operación ${def.tipo}: el transporte PROPIO necesita sincronizar.`);
  }
  registro.set(def.tipo, def);
}

export function definicionDe(tipo: TipoMutacion): DefinicionOperacion | undefined {
  return registro.get(tipo);
}

export function operacionesRegistradas(): readonly DefinicionOperacion[] {
  return [...registro.values()];
}

/** Para el usuario. Un tipo sin registrar (operación antigua de una versión futura) nunca rompe la pantalla. */
export function etiquetaOperacion(tipo: TipoMutacion): string {
  return registro.get(tipo)?.etiqueta ?? 'Operación';
}

/**
 * F14.2 — el nombre de UNA operación concreta, como lo reconoce quien la hizo: «Confirmar limpieza · Habitación 305»,
 * «No conforme · Aseos planta baja». Lo fija quien la registra (`Mutacion.rotuloLocal`); sin él, la etiqueta del tipo.
 */
export function rotuloOperacion(m: Pick<Mutacion, 'tipo' | 'rotuloLocal'>): string {
  return m.rotuloLocal != null && m.rotuloLocal.trim() !== '' ? m.rotuloLocal : etiquetaOperacion(m.tipo);
}

// ----------------------------------------------------------------- cambios en la cola

/**
 * Aviso de «la cola ha cambiado» (operación nueva, resuelta, descartada) para el indicador global y el centro de
 * sincronización. Sólo dice QUE cambió: quien escucha relee lo que necesite.
 */
type EscuchaCola = () => void;
const escuchasCola = new Set<EscuchaCola>();
export function suscribirCambiosCola(f: EscuchaCola): () => void {
  escuchasCola.add(f);
  return () => escuchasCola.delete(f);
}
export function avisarCambioCola(): void {
  for (const f of escuchasCola) f();
}
